export type TrainEventId =
  | 'signal'
  | 'tunnel'
  | 'snow'
  | 'warning'
  | 'landmark'
  | 'message';

export type TrainEvent = {
  id: TrainEventId;
  atSeconds: number;
  label: string;
  detail: string;
  announcement: string;
  asksCoPilot: boolean;
  patch: Partial<Omit<TrainSceneState, 'activeEvent'>>;
};

export type TrainSceneState = {
  location: string;
  weather: string;
  trainSpeedKph: number;
  routeStatus: string;
  visibleLandmarks: string[];
  signalAspect: 'clear' | 'caution' | 'stop' | 'unknown';
  journeyProgress: number;
  activeEvent: Pick<TrainEvent, 'id' | 'label' | 'detail'> | null;
};

export const INITIAL_TRAIN_SCENE: TrainSceneState = {
  location: 'Brassheart Pass',
  weather: 'Light snow, good visibility',
  trainSpeedKph: 62,
  routeStatus: 'Clear road to Frostmere',
  visibleLandmarks: ['Sawtooth peaks ahead', 'Frozen river to port'],
  signalAspect: 'clear',
  journeyProgress: 0,
  activeEvent: null,
};

export const TRAIN_EVENTS: readonly TrainEvent[] = [
  {
    id: 'signal',
    atSeconds: 290,
    label: 'Signal changed',
    detail: 'The North Fork semaphore has dropped from clear to caution.',
    announcement: 'The North Fork signal just changed to caution. Acknowledge it with the co-pilot and ask them to keep watch for the next aspect.',
    asksCoPilot: true,
    patch: {
      trainSpeedKph: 48,
      routeStatus: 'Proceed under caution',
      signalAspect: 'caution',
    },
  },
  {
    id: 'tunnel',
    // The selected Bergensbanen excerpt reaches the visible portal at 5:12.
    atSeconds: 312,
    label: 'Tunnel ahead',
    detail: 'The black stone mouth of Northstar Tunnel is approaching quickly.',
    announcement: 'Northstar Tunnel is approaching. Mention what the co-pilot can see and briefly prepare them for the change in sound and light.',
    asksCoPilot: false,
    patch: {
      location: 'Northstar Tunnel approach',
      routeStatus: 'Tunnel protocol active',
      visibleLandmarks: ['Northstar Tunnel portal', 'Avalanche galleries above the line'],
      signalAspect: 'clear',
    },
  },
  {
    id: 'snow',
    atSeconds: 70,
    label: 'Snow squall',
    detail: 'A hard crosswind is driving snow across the windshield and cutting visibility.',
    announcement: 'A sudden snow squall has reduced visibility. Slow the train in your response and ask whether the co-pilot can still make out the marker lamps.',
    asksCoPilot: true,
    patch: {
      weather: 'Heavy crosswind snow, reduced visibility',
      trainSpeedKph: 38,
      routeStatus: 'Restricted visibility',
      visibleLandmarks: ['Faint marker lamps ahead'],
    },
  },
  {
    id: 'warning',
    atSeconds: 130,
    label: 'Injector warning',
    detail: 'The port water injector is running warmer than its normal range.',
    announcement: 'The port injector temperature has crept high but is not yet dangerous. Ask the co-pilot whether they favor easing steam or watching it for another minute.',
    asksCoPilot: true,
    patch: {
      trainSpeedKph: 34,
      routeStatus: 'Port injector under observation',
    },
  },
  {
    id: 'landmark',
    atSeconds: 185,
    label: 'Unusual landmark',
    detail: 'A silver weather observatory shaped like a great whale hangs over the eastern ridge.',
    announcement: 'The old Moonwhale Observatory has appeared on the ridge. Point it out naturally; you have passed it before, but it is still a strange sight.',
    asksCoPilot: false,
    patch: {
      location: 'Moonwhale Ridge',
      weather: 'Snow easing, broken cloud',
      trainSpeedKph: 46,
      routeStatus: 'Making up time',
      visibleLandmarks: ['Moonwhale Observatory on the eastern ridge', 'Frostmere valley opening ahead'],
    },
  },
  {
    id: 'message',
    atSeconds: 240,
    label: 'Wireless message',
    detail: 'Frostmere Station reports drifting snow on platform two and requests a slow arrival.',
    announcement: 'A wireless message has arrived from Frostmere Station: platform two is drifted in and they request a slow arrival. Relay it to the co-pilot and ask them to confirm the approach plan.',
    asksCoPilot: true,
    patch: {
      location: 'Frostmere outer approach',
      trainSpeedKph: 30,
      routeStatus: 'Slow arrival requested — platform two',
      visibleLandmarks: ['Frostmere lamps in the valley', 'Snowbound platform roofs'],
      signalAspect: 'caution',
    },
  },
];

export function applyTrainEvent(state: TrainSceneState, event: TrainEvent): TrainSceneState {
  return {
    ...state,
    ...event.patch,
    activeEvent: { id: event.id, label: event.label, detail: event.detail },
  };
}

export function journeyProgressForRouteTime(currentTime: number, duration: number): number {
  if (!Number.isFinite(currentTime) || !Number.isFinite(duration) || duration <= 0) return 0;
  return Math.max(0, Math.min(100, (currentTime / duration) * 100));
}

export function playbackRateForTrainSpeed(speedKph: number): number {
  const candidate = Number.isFinite(speedKph) ? speedKph : 62;
  if (candidate <= 0) return 0;
  const speed = Math.max(25, Math.min(80, candidate));
  if (speed <= 62) return 0.7 + ((speed - 25) / 37) * 0.3;
  return 1 + ((speed - 62) / 18) * 0.3;
}

export function nextTrainEventAtTime(
  currentTime: number,
  firedEventIds: ReadonlySet<TrainEventId>,
): TrainEvent | undefined {
  return TRAIN_EVENTS
    .filter((event) => event.atSeconds <= currentTime && !firedEventIds.has(event.id))
    .sort((left, right) => left.atSeconds - right.atSeconds)[0];
}

export function sceneStateForContext(state: TrainSceneState): TrainSceneState {
  return {
    ...state,
    journeyProgress: Math.round(state.journeyProgress),
    trainSpeedKph: Math.round(state.trainSpeedKph),
  };
}
